import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  fetchQuranicTopicDetail,
  fetchQuranicWordDetail,
  fetchBuiltinQuranicTopicDetail,
  fetchBuiltinQuranicWordDetail,
  fetchQuranWordMetadata,
} from "../api";
import QuranicQuiz from "./QuranicQuiz";
import DiamondDivider from "../theme/DiamondDivider";

// ── Quranic word bubble — mirrors WordBubble in TopicDetail ──────────────────

function QuranicWordBubble({ word, anchorRect, onInfoClick, onClose, onPlay, isPlaying }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // Close on outside click, scroll, or Escape — same as WordBubble
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Measure bubble after render, then compute clamped position — same as WordBubble
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const W = el.offsetWidth;
    const H = el.offsetHeight;
    const MARGIN = 8;
    const centerX = anchorRect.left + anchorRect.width / 2;
    const left = Math.max(MARGIN, Math.min(window.innerWidth - W - MARGIN, centerX - W / 2));
    const arrowLeft = Math.max(8, Math.min(W - 20, centerX - left)) - 4;
    const showAbove = anchorRect.top > H + 20;
    setPos({ left, arrowLeft, showAbove, top: showAbove ? anchorRect.top - H - 10 : anchorRect.bottom + 10 });
  }, [anchorRect]);

  const posStyle = pos
    ? { position: "fixed", left: pos.left, top: pos.top, zIndex: 9999 }
    : { position: "fixed", visibility: "hidden", zIndex: 9999 };

  return ReactDOM.createPortal(
    <div ref={ref} style={posStyle} className="bg-slate-800 text-white rounded-lg shadow-2xl px-3 py-2 max-w-[280px] min-w-[120px]">
      {/* Arabic word + transliteration */}
      <p className="text-lg font-arabic text-right leading-snug mb-0.5" dir="rtl">{word.arabic}</p>
      {word.transliteration && (
        <p className="text-xs text-slate-300 italic mb-1">{word.transliteration}</p>
      )}
      {/* Meaning row with play + info buttons */}
      <div className="flex items-center gap-2">
        <p className="flex-1 text-sm leading-snug whitespace-normal text-slate-200">
          {word.loading ? "Loading…" : (word.meaning || "—")}
        </p>
        {/* Play button */}
        <button
          type="button"
          title={isPlaying ? "Stop" : "Play"}
          onClick={() => onPlay(word)}
          className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-dt-gold-bright transition-colors"
        >
          {isPlaying ? (
            <svg className="w-4 h-4 text-dt-gold-bright" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4l14 8-14 8V4z" />
            </svg>
          )}
        </button>
        {/* Info button */}
        {onInfoClick && (
          <button
            type="button"
            title="Full word detail"
            onClick={() => { onInfoClick(); onClose(); }}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-dt-gold-bright transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
          </button>
        )}
      </div>
      {/* Arrow pointing to the word */}
      {pos && (
        <div
          className={`absolute w-2.5 h-2.5 bg-slate-800 rotate-45 ${pos.showAbove ? "bottom-[-5px]" : "top-[-5px]"}`}
          style={{ left: pos.arrowLeft }}
        />
      )}
    </div>,
    document.body
  );
}

// ── Main QuranicTopicDetail component ────────────────────────────────────────

/**
 * QuranicTopicDetail — shows verse text, audio, word tooltips.
 * Completely separate from the existing TopicDetail component.
 * Does NOT use DB words, SRS, or grammar engine.
 * Supports both user-created topics and built-in curriculum topics via isBuiltin prop.
 */
export default function QuranicTopicDetail({ topicId, isBuiltin, onBack, onWordDetail, quizActive, onQuizExit, onBuiltinComplete, onOpenIrab = null }) {
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedVerse, setCopiedVerse] = useState(null); // verse_key with "Copied ✓" showing
  const copyResetRef = useRef(null);
  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  // The ayah as plain text — the same words[]-joined form the iʿrāb prewarm
  // uses, so handing it to the tool is a byte-exact cache hit.
  const verseText = (verse) => verse.words.map((w) => w.arabic).join(" ");

  const copyVerse = async (verse) => {
    try {
      await navigator.clipboard.writeText(verseText(verse));
      setCopiedVerse(verse.verse_key);
      clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopiedVerse(null), 2000);
    } catch {
      window.prompt("Copy this ayah:", verseText(verse));
    }
  };
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isRetake, setIsRetake] = useState(false);
  const [bestScore, setBestScore] = useState(null);
  const [bestTotal, setBestTotal] = useState(null);

  // Word metadata for grammar highlighting
  const [wordMetadata, setWordMetadata] = useState([]); // array of QuranWordMetadata records

  // Grammar highlight toggles: null = off, "verb" | "noun" | "harf" = active filter
  const [grammarFilter, setGrammarFilter] = useState(null);

  // Word bubble state — mirrors TopicDetail's bubble pattern
  const [bubble, setBubble] = useState(null); // { word, anchorRect }
  const [bubbleWordKey, setBubbleWordKey] = useState(null);

  // Audio playback — verse-level
  const audioRef = useRef(null);
  const [playingVerse, setPlayingVerse] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Audio playback — word-level
  const wordAudioRef = useRef(null);
  const [playingWordKey, setPlayingWordKey] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = isBuiltin
          ? await fetchBuiltinQuranicTopicDetail(topicId)
          : await fetchQuranicTopicDetail(topicId);
        setTopic(data);
        setQuizCompleted(data.completed || false);
        setBestScore(data.best_score ?? null);
        setBestTotal(data.best_total ?? null);

        // Load word metadata for grammar highlighting
        // Built-in topics use surah_number; user-created topics use surah_id (both are integers)
        const surahForMetadata = isBuiltin ? data.surah_number : data.surah_id;
        if (surahForMetadata && data.ayah_start && data.ayah_end) {
          try {
            const meta = await fetchQuranWordMetadata(surahForMetadata, data.ayah_start, data.ayah_end);
            setWordMetadata(Array.isArray(meta) ? meta : []);
          } catch {
            setWordMetadata([]);
          }
        }
      } catch (err) {
        setError(err.message || "Could not load topic");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [topicId, isBuiltin]);

  // Build a lookup map: "surah:ayah:position" → grammar_category
  const metaMap = useMemo(() => {
    const map = {};
    for (const m of wordMetadata) {
      map[`${m.surah_number}:${m.ayah_number}:${m.word_position}`] = m.grammar_category;
    }
    return map;
  }, [wordMetadata]);

  const handleWordClick = async (e, word, verseKey, verseTranslation) => {
    const key = `${verseKey}_${word.position}`;
    if (bubbleWordKey === key) {
      setBubble(null);
      setBubbleWordKey(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setBubbleWordKey(key);
    // Show immediately with whatever data we have while loading detail
    setBubble({ anchorRect: rect, word: { loading: true, arabic: word.arabic, transliteration: word.transliteration, meaning: word.meaning, audio_url: word.audio_url, verseKey, verseTranslation, location: word.location, page_number: word.page_number } });
    try {
      const detail = isBuiltin
        ? await fetchBuiltinQuranicWordDetail(topicId, verseKey, word.position)
        : await fetchQuranicWordDetail(topicId, verseKey, word.position);
      if (detail) setBubble((prev) => prev ? { ...prev, word: { ...detail, audio_url: word.audio_url, verseKey, verseTranslation, location: word.location, page_number: word.page_number } } : null);
    } catch {
      setBubble((prev) => prev ? { ...prev, word: { ...prev.word, loading: false } } : null);
    }
  };

  const handlePlayWord = (audioUrl, wordKey) => {
    if (!audioUrl) return;
    if (playingWordKey === wordKey) {
      if (wordAudioRef.current) { wordAudioRef.current.pause(); wordAudioRef.current.src = ""; }
      setPlayingWordKey(null);
      return;
    }
    if (wordAudioRef.current) { wordAudioRef.current.pause(); }
    const audio = new Audio(audioUrl);
    wordAudioRef.current = audio;
    audio.onended = () => setPlayingWordKey(null);
    audio.onerror = () => setPlayingWordKey(null);
    audio.play().then(() => setPlayingWordKey(wordKey)).catch(() => setPlayingWordKey(null));
  };

  const handlePlayVerse = (verse) => {
    setAudioError(null);
    if (!verse.audio_url) {
      setAudioError("No audio available for this verse.");
      return;
    }
    if (playingVerse === verse.verse_key) {
      // Stop
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
      setPlayingVerse(null);
      return;
    }
    if (audioRef.current) { audioRef.current.pause(); }
    const audio = new Audio(verse.audio_url);
    audioRef.current = audio;
    audio.onended = () => setPlayingVerse(null);
    audio.onerror = () => { setAudioError("Could not play audio."); setPlayingVerse(null); };
    audio.play().then(() => setPlayingVerse(verse.verse_key)).catch(() => {
      setAudioError("Could not play audio.");
    });
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <div className="w-8 h-8 border-4 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading verses…</p>
    </div>
  );

  if (error) return (
    <div className="p-4 text-center">
      <p className="text-red-600 mb-4">{error}</p>
      <button onClick={onBack} className="px-4 py-2 bg-slate-200 rounded-lg text-sm">Back</button>
    </div>
  );

  if (!topic) return null;

  if (quizActive && topic) return (
    <QuranicQuiz
      topicId={topicId}
      isBuiltin={isBuiltin}
      retake={isRetake}
      topicName={isBuiltin ? topic.title : `${topic.surah_name} ${topic.ayah_start}–${topic.ayah_end}`}
      surahId={isBuiltin ? topic.surah_number : topic.surah_id}
      ayahStart={topic.ayah_start}
      ayahEnd={topic.ayah_end}
      onExit={() => { setIsRetake(false); onQuizExit(); }}
      onRetake={() => { setIsRetake(true); }}
      onComplete={(s, t) => {
        setIsRetake(false);
        if (s !== undefined && t !== undefined) {
          const passed = t === 0 || s / t >= 0.9;
          if (passed) {
            setQuizCompleted(true);
            setTopic((prev) => prev ? { ...prev, completed: true } : prev);
            if (isBuiltin) onBuiltinComplete && onBuiltinComplete();
          } else {
            setQuizCompleted(false);
            setTopic((prev) => prev ? { ...prev, completed: false } : prev);
            if (isBuiltin) onBuiltinComplete && onBuiltinComplete();
          }
          if (t > 0 && (bestTotal === null || s / t > (bestScore ?? 0) / bestTotal)) {
            setBestScore(s);
            setBestTotal(t);
          }
        }
        onQuizExit();
      }}
    />
  );

  const verses = topic.verses || [];

  // Grammar toggle buttons
  const grammarToggles = [
    { key: "verb", label: "Verb", on: { color: "var(--tag-violet)", background: "var(--tag-violet-bg)", borderColor: "transparent" } },
    { key: "noun", label: "Noun", on: { color: "var(--gold-bright)", background: "var(--gold-bg)", borderColor: "rgba(201,162,39,0.3)" } },
    { key: "harf", label: "Particle", on: { color: "var(--slate)", background: "var(--slate-bg)", borderColor: "transparent" } },
  ];
  const hasMetadata = wordMetadata.length > 0;

  return (
    <div className="dark-scope flex flex-col gap-4 rounded-2xl p-4" style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="hidden md:inline-flex p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-slate-800 truncate">
            {isBuiltin ? topic.title : topic.surah_name}
          </h1>
          <p className="text-sm text-slate-500">Ayahs {topic.ayah_start}–{topic.ayah_end}</p>
        </div>
        {quizCompleted && (
          <span className="flex items-center gap-1 text-xs font-medium text-[var(--tag-teal)] bg-[var(--tag-teal-bg)] border border-[var(--panel-line)] px-2 py-1 rounded-full">
            ✓ Completed{bestScore !== null && bestTotal > 0 && ` · ${bestScore}/${bestTotal} (${Math.round(bestScore/bestTotal*100)}%)`}
          </span>
        )}
        {isBuiltin && topic.curriculum_tier && (
          <span className={`text-xs px-2 py-1 rounded-full font-medium border ${
            topic.curriculum_tier === "core" ? "bg-[var(--tag-teal-bg)] text-[var(--tag-teal)] border-transparent"
            : topic.curriculum_tier === "stretch_70" ? "bg-[var(--gold-bg)] text-dt-gold-bright border-[rgba(201,162,39,0.3)]"
            : "bg-[var(--tag-violet-bg)] text-[var(--tag-violet)] border-transparent"
          }`}>
            {topic.curriculum_tier === "core" ? "Core"
             : topic.curriculum_tier === "stretch_70" ? "Stretch"
             : "Advanced"}
          </span>
        )}
      </div>

      {/* Grammar highlight toggles (only when metadata is available) */}
      {hasMetadata && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-medium">Highlight:</span>
          {grammarToggles.map(({ key, label, on }) => (
            <button
              key={key}
              onClick={() => setGrammarFilter((prev) => prev === key ? null : key)}
              className="text-[0.74rem] px-[11px] py-1 rounded-2xl border transition-colors"
              style={grammarFilter === key
                ? on
                : { color: "var(--parchment-dim)", background: "transparent", borderColor: "var(--panel-line)" }}
            >
              {label}
            </button>
          ))}
          {grammarFilter && (
            <button
              onClick={() => setGrammarFilter(null)}
              className="px-2 py-1 rounded-full text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {/* Verse error */}
      {topic.verse_error && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          ⚠️ {topic.verse_error}
        </div>
      )}

      {audioError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {audioError}
        </div>
      )}

      {/* Quranic word bubble — same portal + useLayoutEffect approach as WordBubble */}
      {bubble && (
        <QuranicWordBubble
          word={bubble.word}
          anchorRect={bubble.anchorRect}
          isPlaying={playingWordKey === bubbleWordKey}
          onPlay={(w) => handlePlayWord(w.audio_url, bubbleWordKey)}
          onInfoClick={onWordDetail ? () => onWordDetail(bubble.word, bubble.word.verseKey, bubble.word.verseTranslation) : null}
          onClose={() => { setBubble(null); setBubbleWordKey(null); }}
        />
      )}

      {/* Verses */}
      {verses.length === 0 ? (
        <div className="p-4 text-center text-slate-500 text-sm">
          No verses loaded. The Quran API may be unavailable.
        </div>
      ) : (
        <div>
          {verses.map((verse, vi) => {
            // Parse surah and ayah from verse_key
            const [vSurah, vAyah] = verse.verse_key.split(":").map(Number);
            return (
              <React.Fragment key={verse.verse_key}>
                <DiamondDivider className="my-4" />
                <div className="overflow-hidden">
                {/* Verse header */}
                <div className="flex items-center justify-between gap-2 px-1 py-1">
                  <span className="text-xs font-medium text-slate-500">Verse {verse.verse_key}</span>
                  <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => copyVerse(verse)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      copiedVerse === verse.verse_key
                        ? "bg-[var(--gold-bg)] text-dt-gold-bright"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    aria-label="Copy this ayah"
                    title="Copy the ayah text"
                  >
                    {copiedVerse === verse.verse_key ? "Copied ✓" : "⧉ Copy"}
                  </button>
                  {onOpenIrab && (
                    <button
                      onClick={() => onOpenIrab(verseText(verse))}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                      aria-label="Open the word-by-word iʿrāb of this ayah"
                      title="Word-by-word structure and iʿrāb"
                    >
                      ◇ Iʿrāb
                    </button>
                  )}
                  <button
                    onClick={() => handlePlayVerse(verse)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      playingVerse === verse.verse_key
                        ? "bg-[var(--gold-bg)] text-dt-gold-bright"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    aria-label={playingVerse === verse.verse_key ? "Stop audio" : "Play verse"}
                  >
                    {playingVerse === verse.verse_key ? (
                      <>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                        Stop
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Play
                      </>
                    )}
                  </button>
                  </div>
                </div>

                {/* Arabic text with clickable words */}
                <div className="px-1 pt-2 pb-1">
                  <div className="flex flex-wrap gap-1 justify-start" dir="rtl">
                    {verse.words.map((word, wi) => {
                      const key = `${verse.verse_key}_${word.position}`;
                      const isActive = bubbleWordKey === key;
                      // Look up grammar category for this word
                      const metaKey = `${vSurah}:${vAyah}:${word.position}`;
                      const grammarCat = metaMap[metaKey];
                      // Type underline shows only when this word's grammar filter
                      // is active (mirrors the scenario dialogue words).
                      const shouldHighlight = grammarFilter && grammarCat === grammarFilter;
                      const underlineClass = shouldHighlight
                        ? ({ verb: "tok-verb", noun: "tok-noun", harf: "tok-prep" }[grammarCat] || "")
                        : "";

                      return (
                        <button
                          key={wi}
                          onClick={(e) => handleWordClick(e, word, verse.verse_key, verse.translation)}
                          className={`tok-word px-2 py-0.5 rounded-lg text-2xl leading-relaxed font-arabic ${
                            isActive ? "tok-selected" : underlineClass
                          }`}
                          title={word.meaning || word.transliteration || ""}
                          dir="rtl"
                        >
                          {word.arabic}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Translation */}
                {verse.translation && (
                  <div className="px-1 pb-1">
                    <p className="text-sm text-slate-500 italic pt-1">
                      {verse.translation}
                    </p>
                  </div>
                )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

    </div>
  );
}
